import {
	ReactElement,
	useContext,
	useMemo,
	useState
} from "react";
import { DestinationWindow } from "client/Api/Apps/common";
import { MagisterLiveInformationProvider } from "./provider";
import { LiveTileContent } from "../LiveTileContent";
import { never } from "client_legacy/ndaw/errors";
import { TileContext } from "../TileContext";
import { Guid } from "client_legacy/ndaw/guid";
import { MagisterResourceKind } from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import { TileTabProps } from "../TileTabSelector";
import { Grades } from "./Grades";
import { Schedule } from "./Schedule";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { MagisterTabDisplayModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type MagisterLiveTileContentProps = {
	connectionId: number;
	destinationWindow: DestinationWindow;
	tabs: readonly MagisterTabDisplayModel[];
	provider: MagisterLiveInformationProvider;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const MagisterLiveTileContent = (props: MagisterLiveTileContentProps): ReactElement => {
	const {
		connectionId,
		destinationWindow,
		tabs,
		provider,
		onOpenTileMenu
	} = props;

	const {
		id,
		name,
		icon,
		dimension,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);

	const userTimezone: TimeZone = useUserTimezone();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const tabProps = useMemo(
		(): readonly TileTabProps[] => {
			return tabs.map(x => {
				const result: TileTabProps = {
					uniqueId: x.uniqueId,
					name: x.name
				};

				return result;
			});
		},
		[tabs]
	);

	const activeTab: MagisterTabDisplayModel | undefined = tabs.find(x => x.uniqueId === activeTabId);

	const renderActiveTab = (): ReactElement | null => {
		if (!activeTab) {
			return null;
		}

		switch (activeTab.resourceKind) {
			case MagisterResourceKind.Grades:
				return (
					<Grades
						tabId={activeTab.uniqueId}
						connectionId={connectionId}
						provider={provider}
						destinationWindow={destinationWindow}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			case MagisterResourceKind.Schedule:
				return (
					<Schedule
						tabId={activeTab.uniqueId}
						connectionId={connectionId}
						provider={provider}
						dimensionToRender={dimension}
						destinationWindow={destinationWindow}
						timezone={userTimezone}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				);
			default:
				never(activeTab.resourceKind);
		}
	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					value={activeTabId}
					onChange={setActiveTabId}
					tabs={tabProps}
					tileId={id}
					tileName={name}
				/>
			</LiveTileContent.Header>

			{renderActiveTab()}
		</LiveTileContent>
	);
};
MagisterLiveTileContent.displayName = "MagisterLiveTileContent";
